import React from 'react';
import { Space, Switch, Tag, Tooltip } from 'antd';
import { EditOutlined, FileProtectOutlined, SendOutlined, UnlockOutlined } from '@ant-design/icons';
import { formatRelativeTime } from '../../../utils/time';
import {
  ActionButton,
  StyledTable,
  StyledTableContainer,
  PaginationText
} from '../styled';

const HCW_STATUS_COLORS = {
  pending: 'gold',
  approved: 'green',
  rejected: 'red',
  suspended: 'volcano'
};

const renderHcwAccess = (record, onToggleHcwAccess, busyUserId) => {
  const access = record.hcwAccess || {};
  const invited = access.lastInvitedAt
    ? `Invited ${formatRelativeTime(new Date(access.lastInvitedAt))}`
    : 'Not invited yet';
  return (
    <Space size={6}>
      <Tooltip title={access.enabled ? invited : 'Give this user access to the Deployed Hearts app'}>
        <Switch
          size="small"
          checked={!!access.enabled}
          loading={busyUserId === record._id}
          onChange={(checked) => onToggleHcwAccess(record, checked)}
        />
      </Tooltip>
      {access.enabled && access.approvalStatus && (
        <Tag color={HCW_STATUS_COLORS[access.approvalStatus] || 'default'} style={{ marginRight: 0 }}>
          {access.approvalStatus.toUpperCase()}
        </Tag>
      )}
    </Space>
  );
};

const getColumns = (
  handleEditUser,
  handleViewWaiver,
  handleUnlockUser,
  onToggleHcwAccess,
  onResendHcwInvite,
  busyUserId
) => [
  {
    title: 'Name',
    dataIndex: 'fullName',
    key: 'fullName',
    width: 180,
    render: (fullName) => fullName || 'N/A',
    sorter: (a, b) => (a.fullName || '').localeCompare(b.fullName || ''),
    ellipsis: true
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: 200,
    ellipsis: true
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'role',
    width: 100,
    render: (role) => {
      const colors = {
        admin: 'red',
        user: 'blue',
        volunteer: 'green'
      };
      return <Tag color={colors[role] || 'default'}>{(role || '').toUpperCase()}</Tag>;
    },
    filters: [
      { text: 'Admin', value: 'admin' },
      { text: 'User', value: 'user' },
      { text: 'Volunteer', value: 'volunteer' },
    ],
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    render: (status, record) => {
      if (status === 'locked' || record.isLocked) {
        const lockInfo = record.lockReason || 'Too many failed login attempts';
        const lockUntil = record.lockUntil ? new Date(record.lockUntil).toLocaleString() : '';
        return (
          <Tooltip title={`${lockInfo}${lockUntil ? ` — Locked until: ${lockUntil}` : ''}`}>
            <Tag color="orange">LOCKED</Tag>
          </Tooltip>
        );
      }
      return (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {(status || 'inactive').toUpperCase()}
        </Tag>
      );
    },
  },
  {
    title: 'Deployed Hearts',
    key: 'hcwAccess',
    width: 160,
    render: (_, record) => renderHcwAccess(record, onToggleHcwAccess, busyUserId)
  },
  {
    title: 'Location',
    key: 'location',
    width: 160,
    render: (_, record) => {
      const info = record.volunteerInfo;
      if (!info) return '-';
      if (info.preferredCity && info.preferredState) {
        return `${info.preferredCity}, ${info.preferredState}`;
      }
      if (info.preferredCity) return info.preferredCity;
      return info.isGeneralVolunteer ? 'Any Location' : '-';
    },
    ellipsis: true
  },
  {
    title: 'Event',
    key: 'selectedEvent',
    width: 200,
    render: (_, record) => {
      const info = record.volunteerInfo;
      if (!info || (!info.selectedEventName && !info.selectedEventId)) {
        return '-';
      }
      const name = info.selectedEventName || 'Selected Event';
      const date = info.selectedEventDate
        ? new Date(info.selectedEventDate).toLocaleDateString()
        : null;
      return (
        <div style={{ lineHeight: 1.3 }}>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {date && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{date}</div>}
        </div>
      );
    },
    ellipsis: true
  },
  {
    title: 'Last Login',
    dataIndex: 'lastLogin',
    key: 'lastLogin',
    width: 140,
    render: (lastLogin) => {
      if (!lastLogin) return 'Never';
      const date = new Date(lastLogin);
      if (isNaN(date.getTime())) return 'Never';
      const full = date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
      const relative = formatRelativeTime(date);
      return <span title={full}>{relative}</span>;
    },
    sorter: (a, b) => {
      const dateA = a.lastLogin ? new Date(a.lastLogin) : new Date(0);
      const dateB = b.lastLogin ? new Date(b.lastLogin) : new Date(0);
      return dateA - dateB;
    },
    ellipsis: true
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 110,
    render: (createdAt) => {
      if (!createdAt) return 'N/A';
      return new Date(createdAt).toLocaleDateString();
    },
    sorter: (a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateA - dateB;
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 240,
    fixed: 'right',
    render: (_, record) => (
      <Space size="small">
        <ActionButton
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditUser(record)}
          className="edit-btn"
          size="small"
        >
          Edit
        </ActionButton>
        {(record.status === 'locked' || record.isLocked) && (
          <ActionButton
            type="link"
            icon={<UnlockOutlined />}
            onClick={() => handleUnlockUser(record)}
            className="edit-btn"
            size="small"
            style={{ color: '#fa8c16' }}
          >
            Unlock
          </ActionButton>
        )}
        {record.hcwAccess?.enabled && (
          <Tooltip title="Re-send the Deployed Hearts invite email and text">
            <ActionButton
              type="link"
              icon={<SendOutlined />}
              onClick={() => onResendHcwInvite(record)}
              className="edit-btn"
              size="small"
            >
              Invite
            </ActionButton>
          </Tooltip>
        )}
        {record.role === 'volunteer' && (
          <ActionButton
            type="link"
            icon={<FileProtectOutlined />}
            onClick={() => handleViewWaiver(record)}
            className="edit-btn"
            size="small"
            style={{ color: '#52c41a' }}
          >
            Waiver
          </ActionButton>
        )}
      </Space>
    ),
  },
];

const UserDirectoryTable = ({
  users,
  loading,
  pagination,
  onPaginationChange,
  onEditUser,
  onViewWaiver,
  onUnlockUser,
  onToggleHcwAccess,
  onResendHcwInvite,
  hcwBusyUserId,
  selectedRowKeys,
  onSelectionChange
}) => {
  const columns = getColumns(
    onEditUser,
    onViewWaiver,
    onUnlockUser,
    onToggleHcwAccess,
    onResendHcwInvite,
    hcwBusyUserId
  );

  return (
    <div className="table-animate-in">
      <StyledTableContainer>
        <StyledTable
          className="user-management-pagination"
          columns={columns}
          dataSource={users}
          rowKey="_id"
          rowSelection={onSelectionChange ? {
            selectedRowKeys,
            onChange: (keys, rows) => onSelectionChange(keys, rows),
            preserveSelectedRowKeys: true
          } : undefined}
          loading={loading}
          size="middle"
          locale={{
            emptyText: loading ? 'Loading users...' : 'No users found'
          }}
          components={{
            header: {
              cell: (props) => (
                <th 
                  {...props} 
                  style={{ 
                    ...props.style, 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }} 
                />
              )
            }
          }}
          scroll={{ x: 1440 }}
          pagination={{
            ...pagination,
            onChange: onPaginationChange,
            onShowSizeChange: onPaginationChange,
            showTotal: (total, range) => (
              <PaginationText>
                Showing {range[0]}-{range[1]} of {total} users
              </PaginationText>
            ),
            className: 'user-management-pagination'
          }}
        />
      </StyledTableContainer>
    </div>
  );
};

export default UserDirectoryTable;
